<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import Input from 'v3/components/Form/Input.vue';
import { ArrowLeft, ArrowRight, Sparkles } from 'lucide-vue-next';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-shadcn/new-york/ui/tabs';

const store = useStore();
const router = useRouter();
const route = useRoute();

// Google Sheets campaign data (template-based flow)
const gsCampaignData = computed(
  () => store.getters['campaigns/getCampaignData']
);

// Detect source type: query param OR saved data in store
const isGoogleSheets = computed(() => {
  if (route.query.sourceType === 'google_sheets') return true;
  const fd = gsCampaignData.value?.filterData;
  return fd?.source_type === 'google_sheets';
});

const isDatabase = computed(() => {
  if (route.query.sourceType === 'database') return true;
  const fd = gsCampaignData.value?.filterData;
  return fd?.source_type === 'database';
});

// Template-based flow: either Google Sheets or Database
const isTemplateBased = computed(() => isGoogleSheets.value || isDatabase.value);

// Estado para o nome da campanha
const campaignName = ref('');

const { currentUser } = store.state.auth;

// Estado para o modal de processamento - simplificado
const isProcessing = ref(false);

// Estado para armazenar os dados do público
const publicoData = ref(null);

// Tab state
const activeTab = ref('details');
const tabs = [
  {
    id: 'details',
    label: 'Detalhes da Campanha',
    shortLabel: 'Detalhes',
    icon: '📝',
    title: 'Detalhes da Campanha',
    description: 'Defina o nome para sua campanha WhatsApp',
  },
  {
    id: 'summary',
    label: 'Resumo Completo',
    shortLabel: 'Resumo',
    icon: '📋',
    title: 'Resumo da Campanha',
    description: 'Revise todas as configurações antes de criar',
  },
];

// ✅ Estado para armazenar os dados da campanha WhatsApp normal
const campaignData = computed(
  () => store.getters['campaigns/getWhatsappNormalCampaignData']
);

// Tab validation
const tabValidation = computed(() => ({
  details: !!campaignName.value,
  summary: true,
}));

// Tab navigation helpers
const canNavigateToTab = tabId => {
  const tabIndex = tabs.findIndex(tab => tab.id === tabId);
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);

  if (tabIndex <= currentIndex) return true;

  // Check if previous tabs are valid
  for (let i = 0; i < tabIndex; i += 1) {
    const tab = tabs[i];
    if (!tabValidation.value[tab.id]) {
      return false;
    }
  }
  return true;
};

// Get current tab data
const currentTabData = computed(() => {
  return tabs.find(tab => tab.id === activeTab.value);
});

// Função para carregar dados do público
const loadPublicoData = async () => {
  try {
    const audience = {
      labels: (campaignData.value.selectedLabels || []).map(id => ({
        id,
        type: 'Label',
      })),
    };

    const publico = await store.dispatch('campaigns/previewAudience', {
      audience,
      limit: 200,
    });

    publicoData.value = publico;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao carregar público:', error);
    publicoData.value = null;
  }
};

const onTabChange = async tabId => {
  if (!canNavigateToTab(tabId)) return;
  activeTab.value = tabId;

  // Carregar dados do público quando navegar para o summary (apenas tag-based)
  if (tabId === 'summary' && !publicoData.value && !isTemplateBased.value) {
    await loadPublicoData();
  }
};

const nextTab = async () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex < tabs.length - 1) {
    const nextTabId = tabs[currentIndex + 1].id;
    await onTabChange(nextTabId);
  }
};

const previousTab = async () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex > 0) {
    const prevTabId = tabs[currentIndex - 1].id;
    activeTab.value = prevTabId;
  }
};

// Função para formatar datas para o padrão brasileiro (DD/MM/AAAA)
const formatDate = dateString => {
  if (!dateString) return '';
  // Append T00:00:00 to force local time parsing (date-only strings are parsed as UTC)
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Função para formatar override display (evita {{ }} no template Vue)
const formatOverrideDisplay = override => {
  if (!override) return '';
  if (override.mode === 'column') {
    return '{{' + override.value + '}}';
  }
  return override.value;
};

// Função para traduzir o tipo de recorrência
const translateRecurrenceType = recurrenceType => {
  const recurrenceMap = {
    daily: 'Diária',
    weekly: 'Semanal',
    monthly: 'Mensal',
    custom: 'Personalizada',
  };
  return recurrenceMap[recurrenceType] || recurrenceType;
};

// ✅ Computeds para dados de resumo - adaptado para WhatsApp normal
const summaryData = computed(() => {
  const data = campaignData.value;

  if (!data) return null;

  return {
    config: {
      selectedLabels: data.selectedLabels || [],
      selectedInbox: data.selectedInbox || null,
      webhookUrl: data.webhookUrl || '',
    },
    message: {
      content: data.message || '',
      attachedFiles: data.attachedFiles || [],
    },
    schedule: data.schedule || {},
  };
});

// Google Sheets summary
const gsSummaryData = computed(() => {
  const data = gsCampaignData.value;
  if (!data || !data.filterData) return null;
  const fd = data.filterData;
  return {
    template: data.template || null,
    spreadsheet: fd.spreadsheet_name || '',
    sheet: fd.sheet_name || '',
    phoneColumn: fd.send_message?.phone_column || '',
    nameColumn: fd.send_message?.name_column || '',
    placeholders: fd.placeholders || {},
    interactiveOverrides: fd.interactive_overrides || null,
    schedule: data.schedule || {},
  };
});

// Database summary
const dbSummaryData = computed(() => {
  const data = gsCampaignData.value;
  if (!data) return null;
  return {
    template: data.template || null,
    selectedTable: data.selectedTable || '',
    phoneColumn: data.selectedPhoneColumn || '',
    contactColumn: data.selectedContactColumn || '',
    placeholders: data.placeholders || {},
    filters: data.filters || [],
    schedule: data.schedule || {},
  };
});

// Função simplificada para fazer upload dos arquivos
const uploadAttachedFiles = async attachedFiles => {
  if (!attachedFiles || attachedFiles.length === 0) {
    return [];
  }

  const validFiles = attachedFiles.filter(fileItem => fileItem.resource?.file);

  const uploadedFiles = await Promise.all(
    validFiles.map(async fileItem => {
      const file = fileItem.resource.file;
      const fileName = fileItem.resource.name || file.name || 'arquivo';
      const uploadResult = await uploadFile(file, store.state.auth.accountId);
      return {
        download_url: uploadResult.fileUrl,
        name: fileName,
      };
    })
  );

  return uploadedFiles;
};

// ✅ Criar campanha WhatsApp normal - SIMPLIFICADA
const createCampaign = async () => {
  if (!campaignName.value) {
    useAlert('Por favor, insira o nome da campanha!');
    return;
  }

  try {
    isProcessing.value = true;

    // ✅ Salvar nome da campanha e tag no store
    await store.dispatch('campaigns/saveWhatsappNormalCampaignData', {
      campaignName: campaignName.value,
    });

    const currentCampaignData =
      store.getters['campaigns/getWhatsappNormalCampaignData'];

    // Fazer upload dos arquivos anexados
    const uploadedFiles = await uploadAttachedFiles(
      currentCampaignData.attachedFiles
    );

    // Payload para criar campanha
    const campaignPayload = {
      title: campaignName.value,
      message: currentCampaignData.message || '',
      inbox_id: currentCampaignData.selectedInbox,
      sender_id: currentUser.id,
      audience: {
        labels: (currentCampaignData.selectedLabels || []).map(labelId => ({
          id: labelId,
          type: 'Label',
        })),
        webhook_url: currentCampaignData.webhookUrl || '',
        attached_files: uploadedFiles,
      },
      campaign_type: 'whatsapp_bayles',
      scheduled_dates: currentCampaignData.schedule?.scheduledDates || [],
      filter_data: { source_type: 'tag' },
      templateParams: {},
    };

    await store.dispatch('campaigns/create', campaignPayload);

    // Limpar dados após criar campanha
    store.dispatch('campaigns/clearWhatsappNormalCampaignData');

    router.push({ name: 'campaigns_whatsapp_app_schedule' });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao criar campanha WhatsApp:', error);

    let errorMessage =
      'Ocorreu um erro ao criar a campanha WhatsApp. Tente novamente.';
    if (error.response?.data?.errors) {
      errorMessage = Array.isArray(error.response.data.errors)
        ? error.response.data.errors.join(', ')
        : error.response.data.errors;
    } else if (error.message) {
      errorMessage = error.message;
    }

    useAlert(errorMessage);
  } finally {
    isProcessing.value = false;
  }
};

// ✅ Criar campanha Google Sheets (WhatsApp Official)
const createGoogleSheetsCampaign = async () => {
  if (!campaignName.value) {
    useAlert('Por favor, insira o nome da campanha!');
    return;
  }

  try {
    isProcessing.value = true;

    const data = gsCampaignData.value;
    const fd = data.filterData;
    const template = data.template;

    const campaignPayload = {
      title: campaignName.value,
      message: template?.content || '',
      inbox_id: template?.inbox_id,
      sender_id: currentUser.id,
      campaign_type: 'whatsapp_bayles',
      audience: {},
      scheduled_dates: data.schedule?.scheduledDates || [],
      filter_data: fd,
      template_params: {
        template_id: template?.id,
        name: template?.name,
      },
    };

    await store.dispatch('campaigns/create', campaignPayload);
    store.dispatch('campaigns/clearCampaignData');

    router.push({ name: 'campaigns_whatsapp_app_schedule' });
  } catch (error) {
    useAlert(
      error.response?.data?.error || error.message || 'Erro ao criar campanha'
    );
  } finally {
    isProcessing.value = false;
  }
};

// ✅ Criar campanha Database (WhatsApp App)
const createDatabaseCampaign = async () => {
  if (!campaignName.value) {
    useAlert('Por favor, insira o nome da campanha!');
    return;
  }

  try {
    isProcessing.value = true;

    const data = gsCampaignData.value;
    const template = data.template;

    // Build filters following the official CampaignFinish pattern
    const filters = (data.filters || []).map(filterItem => ({
      attribute_key: filterItem.attributeKey,
      values: filterItem.values,
      filter_operator: filterItem.filterOperator,
      query_operator: filterItem.queryOperator,
      group_index: filterItem.groupIndex,
      attribute_model: filterItem.attributeModel,
    }));

    const filterData = {
      source_type: 'database',
      tabela: data.selectedTable,
      placeholders: data.placeholders || {},
      send_message: {
        name_column: data.selectedContactColumn,
        phone_column: data.selectedPhoneColumn,
        email_column: null,
        label_id: 0,
      },
      filtros: filters,
    };

    const campaignPayload = {
      title: campaignName.value,
      message:
        template?.components?.find(comp => comp.type === 'BODY')?.text ||
        template?.content ||
        '',
      inbox_id: template?.inboxId || template?.inbox_id,
      sender_id: currentUser.id,
      campaign_type: 'whatsapp_bayles',
      audience: {},
      scheduled_dates: data.schedule?.scheduledDates || [],
      filter_data: filterData,
      template_params: {
        template_id: template?.id,
        name: template?.name,
      },
    };

    await store.dispatch('campaigns/create', campaignPayload);
    store.dispatch('campaigns/clearCampaignData');

    router.push({ name: 'campaigns_whatsapp_app_schedule' });
  } catch (error) {
    useAlert(
      error.response?.data?.error || error.message || 'Erro ao criar campanha'
    );
  } finally {
    isProcessing.value = false;
  }
};

// ✅ Wrapper: despacha para o método correto baseado no tipo de campanha
const submitCampaign = async () => {
  if (isDatabase.value) {
    await createDatabaseCampaign();
  } else if (isGoogleSheets.value) {
    await createGoogleSheetsCampaign();
  } else {
    await createCampaign();
  }
};

// Computed para verificar se pode submeter
const canSubmit = computed(() => {
  return !!campaignName.value;
});

// ✅ Voltar para agendamento
const goBack = () => {
  router.push({
    name: 'campaigns_whatsapp_app_schedule_step',
    params: { accountId: store.state.auth.accountId },
  });
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
<template>
  <div
    class="border border-woot-400 dark:border-woot-400 bg-n-background min-h-[100px] p-6 w-full min-w-0 flex-1 flex flex-col overflow-y-auto"
  >
    <!-- Modal de Processamento - SIMPLIFICADO -->
    <div
      v-if="isProcessing"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        class="bg-white dark:bg-slate-900 p-8 rounded-lg shadow-2xl flex flex-col items-center max-w-md w-full mx-4"
      >
        <div class="spinner mb-4" />
        <h3 class="text-lg font-semibold text-n-slate-12 dark:text-white mb-2">
          Criando Campanhas
        </h3>
        <p class="text-sm text-n-slate-10 dark:text-n-slate-11 text-center">
          Aguarde enquanto sua campanha é criada...
        </p>
      </div>
    </div>

    <!-- Header -->
    <div class="page-header-container mb-4 sm:mb-6 flex-shrink-0">
      <PageHeader
        class="w-full max-w-4xl"
        header-title="Revisar e Finalizar Campanha WhatsApp"
        header-content="Revise todos os detalhes da sua campanha WhatsApp antes de criar. Você poderá acompanhar o progresso após a criação."
      />
    </div>

    <!-- Main Content with Tabs -->
    <div class="w-full flex flex-col">
      <Tabs
        :model-value="activeTab"
        class="w-full h-full flex flex-col"
        @update:model-value="onTabChange"
      >
        <!-- Tab Navigation -->
        <TabsList class="tab-list">
          <TabsTrigger
            v-for="tab in tabs"
            :key="tab.id"
            :value="tab.id"
            :disabled="!canNavigateToTab(tab.id)"
            class="relative transition-all duration-200 text-xs sm:text-sm font-medium"
            :class="[
              !canNavigateToTab(tab.id) && 'opacity-50 cursor-not-allowed',
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 sm:gap-2 min-w-0"
            >
              <span class="text-sm sm:text-base flex-shrink-0">{{
                tab.icon
              }}</span>
              <span class="hidden sm:block truncate">{{ tab.label }}</span>
              <span class="sm:hidden truncate">{{ tab.shortLabel }}</span>
              <span
                v-if="tabValidation[tab.id]"
                class="text-green-500 dark:text-green-400 ml-1 flex-shrink-0"
              >
                ✓
              </span>
            </div>
          </TabsTrigger>
        </TabsList>

        <!-- Tab Content -->
        <div class="mt-4">
          <!-- Details Tab -->
          <TabsContent value="details" class="space-y-4 sm:space-y-6">
            <div class="modern-tab-container">
              <!-- Decorative Background Elements -->
              <div class="background-decoration">
                <div class="decoration-blob decoration-blob-1" />
                <div class="decoration-blob decoration-blob-2" />
              </div>

              <!-- Header Section -->
              <div class="tab-header">
                <div class="header-content">
                  <div class="accent-line" />
                  <div class="min-w-0 flex-1">
                    <h3
                      class="text-lg font-semibold text-n-slate-12 dark:text-white mb-2"
                    >
                      {{ currentTabData?.title }}
                    </h3>
                    <p class="description-text">
                      {{ currentTabData?.description }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Main Content Area -->
              <div class="main-content-area">
                <!-- Form Content -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Input
                      v-model="campaignName"
                      label="Nome da Campanha"
                      placeholder="Digite o nome da campanha"
                      :disabled="isProcessing"
                      class="w-full"
                    />
                  </div>
                </div>
              </div>

              <!-- Action Bar -->
              <div class="action-bar">
                <div class="action-bar-background" />

                <!-- Back Button -->
                <div class="back-section">
                  <button
                    type="button"
                    :disabled="isProcessing"
                    class="modern-button back-button"
                    @click="goBack"
                  >
                    <div class="button-shine" />
                    <ArrowLeft class="button-icon" />
                    <span class="hidden sm:inline"
                      >Retornar para Agendamento</span
                    >
                    <span class="sm:hidden">Retornar</span>
                  </button>
                </div>

                <!-- Center Actions -->
                <div class="center-actions" />

                <!-- Primary Actions -->
                <div class="primary-actions">
                  <button
                    type="button"
                    :disabled="!tabValidation.details"
                    class="modern-button primary-button"
                    @click="nextTab"
                  >
                    <div class="primary-gradient" />
                    <div class="primary-glow" />
                    <div class="button-content">
                      <span class="button-text">
                        <span class="hidden sm:inline"
                          >Próximo: Ver Resumo</span
                        >
                        <span class="sm:hidden">Próximo</span>
                        <ArrowRight class="button-icon ml-2" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>

          <!-- Summary Tab -->
          <TabsContent value="summary" class="h-full flex flex-col">
            <div class="modern-tab-container h-full">
              <!-- Decorative Background Elements -->
              <div class="background-decoration">
                <div class="decoration-blob decoration-blob-1" />
                <div class="decoration-blob decoration-blob-2" />
              </div>

              <!-- Header Section -->
              <div class="tab-header">
                <div class="header-content">
                  <div class="accent-line" />
                  <div class="min-w-0 flex-1">
                    <h3
                      class="text-lg font-semibold text-n-slate-12 dark:text-white mb-2"
                    >
                      {{ currentTabData?.title }}
                    </h3>
                    <p class="description-text">
                      {{ currentTabData?.description }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Main Content Area -->
              <div
                class="main-content-area flex-1 overflow-y-auto custom-scrollbar"
              >
                <!-- ====== GOOGLE SHEETS SUMMARY ====== -->
                <template v-if="isGoogleSheets && gsSummaryData">
                  <div class="space-y-6 pb-4">
                    <!-- Resumo: Fonte de Dados (Google Sheets) -->
                    <div
                      class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                    >
                      <div
                        class="flex items-center gap-3 pb-4 border-b border-woot-400 dark:border-woot-400 mb-4"
                      >
                        <div
                          class="w-10 h-10 bg-n-blue-3 dark:bg-n-blue-9 rounded-full flex items-center justify-center flex-shrink-0"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span class="text-lg">📊</span>
                        </div>
                        <div>
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <h3
                            class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-11"
                          >
                            Fonte de Dados
                          </h3>
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <p
                            class="text-sm text-n-slate-10 dark:text-n-slate-11"
                          >
                            Google Sheets - Planilha e mapeamento
                          </p>
                        </div>
                      </div>

                      <div class="grid grid-cols-2 gap-4 text-sm">
                        <div
                          class="p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span
                            class="text-xs text-n-slate-10 dark:text-n-slate-11"
                            >Planilha</span
                          >
                          <p
                            class="font-medium text-n-slate-12 dark:text-white mt-1"
                          >
                            {{ gsSummaryData.spreadsheet }}
                          </p>
                        </div>
                        <div
                          class="p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span
                            class="text-xs text-n-slate-10 dark:text-n-slate-11"
                            >Aba</span
                          >
                          <p
                            class="font-medium text-n-slate-12 dark:text-white mt-1"
                          >
                            {{ gsSummaryData.sheet }}
                          </p>
                        </div>
                        <div
                          class="p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span
                            class="text-xs text-n-slate-10 dark:text-n-slate-11"
                            >Coluna Telefone</span
                          >
                          <p
                            class="font-medium text-n-slate-12 dark:text-white mt-1"
                          >
                            {{ gsSummaryData.phoneColumn }}
                          </p>
                        </div>
                        <div
                          class="p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span
                            class="text-xs text-n-slate-10 dark:text-n-slate-11"
                            >Coluna Nome</span
                          >
                          <p
                            class="font-medium text-n-slate-12 dark:text-white mt-1"
                          >
                            {{ gsSummaryData.nameColumn }}
                          </p>
                        </div>
                      </div>

                      <!-- Placeholders -->
                      <div
                        v-if="
                          Object.keys(gsSummaryData.placeholders).length > 0
                        "
                        class="mt-4 p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                      >
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                        <span
                          class="text-sm font-medium text-n-slate-11 dark:text-n-slate-11"
                          >Placeholders:</span
                        >
                        <div class="mt-2 flex flex-wrap gap-2">
                          <span
                            v-for="(value, key) in gsSummaryData.placeholders"
                            :key="key"
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-n-blue-3 dark:bg-n-blue-9 text-n-blue-11 dark:text-n-blue-3 border border-n-blue-6 dark:border-n-blue-7"
                          >
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                            {{ key }} &rarr; {{ value }}
                          </span>
                        </div>
                      </div>

                      <!-- Interactive Overrides -->
                      <div
                        v-if="gsSummaryData.interactiveOverrides"
                        class="mt-4 p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                      >
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                        <span
                          class="text-sm font-medium text-n-slate-11 dark:text-n-slate-11"
                          >Campos Interativos:</span
                        >
                        <div class="mt-2 space-y-2 text-sm">
                          <div
                            v-if="
                              gsSummaryData.interactiveOverrides.title?.value
                            "
                            class="flex items-center gap-2"
                          >
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                            <span class="text-n-slate-10 dark:text-n-slate-11"
                              >Titulo:</span
                            >
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-n-teal-3 dark:bg-n-teal-9 text-n-teal-11 dark:text-n-teal-3 border border-n-teal-6 dark:border-n-teal-7"
                            >
                              {{
                                formatOverrideDisplay(
                                  gsSummaryData.interactiveOverrides.title
                                )
                              }}
                            </span>
                          </div>
                          <div
                            v-if="
                              gsSummaryData.interactiveOverrides.footer?.value
                            "
                            class="flex items-center gap-2"
                          >
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                            <span class="text-n-slate-10 dark:text-n-slate-11"
                              >Rodape:</span
                            >
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-n-teal-3 dark:bg-n-teal-9 text-n-teal-11 dark:text-n-teal-3 border border-n-teal-6 dark:border-n-teal-7"
                            >
                              {{
                                formatOverrideDisplay(
                                  gsSummaryData.interactiveOverrides.footer
                                )
                              }}
                            </span>
                          </div>
                          <div
                            v-if="
                              gsSummaryData.interactiveOverrides.mediaUrl?.value
                            "
                            class="flex items-center gap-2"
                          >
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                            <span class="text-n-slate-10 dark:text-n-slate-11"
                              >Media URL:</span
                            >
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-n-teal-3 dark:bg-n-teal-9 text-n-teal-11 dark:text-n-teal-3 border border-n-teal-6 dark:border-n-teal-7"
                            >
                              {{
                                formatOverrideDisplay(
                                  gsSummaryData.interactiveOverrides.mediaUrl
                                )
                              }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Resumo: Agendamento (Google Sheets) -->
                    <div
                      class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                    >
                      <div
                        class="flex items-center gap-3 pb-4 border-b border-woot-400 dark:border-woot-400 mb-4"
                      >
                        <div
                          class="w-10 h-10 bg-n-iris-3 dark:bg-n-iris-9 rounded-full flex items-center justify-center flex-shrink-0"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span class="text-lg">📅</span>
                        </div>
                        <div>
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <h3
                            class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-2"
                          >
                            Agendamento
                          </h3>
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <p
                            class="text-sm text-n-slate-10 dark:text-n-slate-11"
                          >
                            Quando a campanha será executada
                          </p>
                        </div>
                      </div>

                      <div
                        v-if="
                          gsSummaryData.schedule &&
                          Object.keys(gsSummaryData.schedule).length > 0
                        "
                        class="space-y-3"
                      >
                        <div
                          v-if="gsSummaryData.schedule.startDate"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Período:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-iris-3 dark:bg-n-iris-9 text-n-iris-11 dark:text-n-iris-3 border border-n-iris-6 dark:border-n-iris-7"
                          >
                            {{ formatDate(gsSummaryData.schedule.startDate) }}
                          </span>
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >até</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-iris-3 dark:bg-n-iris-9 text-n-iris-11 dark:text-n-iris-3 border border-n-iris-6 dark:border-n-iris-7"
                          >
                            {{ formatDate(gsSummaryData.schedule.endDate) }}
                          </span>
                        </div>

                        <div
                          v-if="gsSummaryData.schedule.fromTime"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Horário:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-blue-3 dark:bg-n-blue-9 text-n-blue-11 dark:text-n-blue-3 border border-n-blue-6 dark:border-n-blue-7"
                          >
                            {{ gsSummaryData.schedule.fromTime }}
                          </span>
                        </div>

                        <div
                          v-if="gsSummaryData.schedule.recurrenceType"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Recorrência:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-amber-3 dark:bg-n-amber-9 text-n-amber-11 dark:text-n-amber-3 border border-n-amber-6 dark:border-n-amber-7"
                          >
                            {{
                              translateRecurrenceType(
                                gsSummaryData.schedule.recurrenceType
                              )
                            }}
                          </span>
                        </div>

                        <div
                          v-if="gsSummaryData.schedule.scheduledDates?.length"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Total de execuções:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-teal-3 dark:bg-n-teal-9 text-n-teal-11 dark:text-n-teal-3 border border-n-teal-6 dark:border-n-teal-7 font-bold"
                          >
                            {{ gsSummaryData.schedule.scheduledDates.length }}
                          </span>
                        </div>
                      </div>
                      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                      <div
                        v-else
                        class="text-sm text-n-slate-10 dark:text-n-slate-11 italic"
                      >
                        Nenhum agendamento configurado
                      </div>
                    </div>
                  </div>
                </template>

                <!-- ====== DATABASE SUMMARY ====== -->
                <template v-else-if="isDatabase && dbSummaryData">
                  <div class="space-y-6 pb-4">
                    <!-- Resumo: Fonte de Dados (Database) -->
                    <div
                      class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                    >
                      <div
                        class="flex items-center gap-3 pb-4 border-b border-woot-400 dark:border-woot-400 mb-4"
                      >
                        <div
                          class="w-10 h-10 bg-n-blue-3 dark:bg-n-blue-9 rounded-full flex items-center justify-center flex-shrink-0"
                        >
                          <span class="text-lg">🗄️</span>
                        </div>
                        <div>
                          <h3
                            class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-11"
                          >
                            Fonte de Dados
                          </h3>
                          <p
                            class="text-sm text-n-slate-10 dark:text-n-slate-11"
                          >
                            Banco de Dados - Tabela e mapeamento
                          </p>
                        </div>
                      </div>

                      <div class="grid grid-cols-2 gap-4 text-sm">
                        <div
                          class="p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <span
                            class="text-xs text-n-slate-10 dark:text-n-slate-11"
                            >Tabela</span
                          >
                          <p
                            class="font-medium text-n-slate-12 dark:text-white mt-1"
                          >
                            {{ dbSummaryData.selectedTable }}
                          </p>
                        </div>
                        <div
                          class="p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <span
                            class="text-xs text-n-slate-10 dark:text-n-slate-11"
                            >Coluna Telefone</span
                          >
                          <p
                            class="font-medium text-n-slate-12 dark:text-white mt-1"
                          >
                            {{ dbSummaryData.phoneColumn }}
                          </p>
                        </div>
                        <div
                          class="p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <span
                            class="text-xs text-n-slate-10 dark:text-n-slate-11"
                            >Coluna Contato</span
                          >
                          <p
                            class="font-medium text-n-slate-12 dark:text-white mt-1"
                          >
                            {{ dbSummaryData.contactColumn }}
                          </p>
                        </div>
                      </div>

                      <!-- Placeholders -->
                      <div
                        v-if="
                          Object.keys(dbSummaryData.placeholders).length > 0
                        "
                        class="mt-4 p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                      >
                        <span
                          class="text-sm font-medium text-n-slate-11 dark:text-n-slate-11"
                          >Placeholders:</span
                        >
                        <div class="mt-2 flex flex-wrap gap-2">
                          <span
                            v-for="(value, key) in dbSummaryData.placeholders"
                            :key="key"
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-n-blue-3 dark:bg-n-blue-9 text-n-blue-11 dark:text-n-blue-3 border border-n-blue-6 dark:border-n-blue-7"
                          >
                            {{ key }} &rarr; {{ value }}
                          </span>
                        </div>
                      </div>

                      <!-- Filters -->
                      <div
                        v-if="
                          dbSummaryData.filters &&
                          dbSummaryData.filters.length > 0
                        "
                        class="mt-4 p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                      >
                        <span
                          class="text-sm font-medium text-n-slate-11 dark:text-n-slate-11"
                          >Filtros aplicados:</span
                        >
                        <div class="mt-2 space-y-2">
                          <div
                            v-for="filter in dbSummaryData.filters"
                            :key="filter.attributeKey"
                            class="flex flex-wrap items-center gap-2 text-xs"
                          >
                            <span
                              class="inline-flex items-center px-2 py-1 rounded-full bg-n-amber-3 dark:bg-n-amber-9 text-n-amber-11 dark:text-n-amber-3 border border-n-amber-6 dark:border-n-amber-7"
                            >
                              {{ filter.attributeKey }}
                            </span>
                            <span
                              class="text-n-slate-10 dark:text-n-slate-11"
                            >
                              {{ filter.filterOperator }}
                            </span>
                            <span
                              class="inline-flex items-center px-2 py-1 rounded-full bg-n-teal-3 dark:bg-n-teal-9 text-n-teal-11 dark:text-n-teal-3 border border-n-teal-6 dark:border-n-teal-7"
                            >
                              {{
                                Array.isArray(filter.values)
                                  ? filter.values.join(', ')
                                  : filter.values
                              }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Resumo: Agendamento (Database) -->
                    <div
                      class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                    >
                      <div
                        class="flex items-center gap-3 pb-4 border-b border-woot-400 dark:border-woot-400 mb-4"
                      >
                        <div
                          class="w-10 h-10 bg-n-iris-3 dark:bg-n-iris-9 rounded-full flex items-center justify-center flex-shrink-0"
                        >
                          <span class="text-lg">📅</span>
                        </div>
                        <div>
                          <h3
                            class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-2"
                          >
                            Agendamento
                          </h3>
                          <p
                            class="text-sm text-n-slate-10 dark:text-n-slate-11"
                          >
                            Quando a campanha será executada
                          </p>
                        </div>
                      </div>

                      <div
                        v-if="
                          dbSummaryData.schedule &&
                          Object.keys(dbSummaryData.schedule).length > 0
                        "
                        class="space-y-3"
                      >
                        <div
                          v-if="dbSummaryData.schedule.startDate"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Período:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-iris-3 dark:bg-n-iris-9 text-n-iris-11 dark:text-n-iris-3 border border-n-iris-6 dark:border-n-iris-7"
                          >
                            {{ formatDate(dbSummaryData.schedule.startDate) }}
                          </span>
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >até</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-iris-3 dark:bg-n-iris-9 text-n-iris-11 dark:text-n-iris-3 border border-n-iris-6 dark:border-n-iris-7"
                          >
                            {{ formatDate(dbSummaryData.schedule.endDate) }}
                          </span>
                        </div>

                        <div
                          v-if="dbSummaryData.schedule.fromTime"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Horário:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-blue-3 dark:bg-n-blue-9 text-n-blue-11 dark:text-n-blue-3 border border-n-blue-6 dark:border-n-blue-7"
                          >
                            {{ dbSummaryData.schedule.fromTime }}
                          </span>
                        </div>

                        <div
                          v-if="dbSummaryData.schedule.recurrenceType"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Recorrência:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-amber-3 dark:bg-n-amber-9 text-n-amber-11 dark:text-n-amber-3 border border-n-amber-6 dark:border-n-amber-7"
                          >
                            {{
                              translateRecurrenceType(
                                dbSummaryData.schedule.recurrenceType
                              )
                            }}
                          </span>
                        </div>

                        <div
                          v-if="dbSummaryData.schedule.scheduledDates?.length"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Total de execuções:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-teal-3 dark:bg-n-teal-9 text-n-teal-11 dark:text-n-teal-3 border border-n-teal-6 dark:border-n-teal-7 font-bold"
                          >
                            {{ dbSummaryData.schedule.scheduledDates.length }}
                          </span>
                        </div>
                      </div>
                      <div
                        v-else
                        class="text-sm text-n-slate-10 dark:text-n-slate-11 italic"
                      >
                        Nenhum agendamento configurado
                      </div>
                    </div>
                  </div>
                </template>

                <!-- ====== TAG-BASED SUMMARY (original) ====== -->
                <template v-else>
                  <div class="space-y-6 pb-4">
                    <!-- Resumo: Configuração -->
                    <div
                      class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                    >
                      <div
                        class="flex items-center gap-3 pb-4 border-b border-woot-400 dark:border-woot-400 mb-4"
                      >
                        <div
                          class="w-10 h-10 bg-n-blue-3 dark:bg-n-blue-9 rounded-full flex items-center justify-center flex-shrink-0"
                        >
                          <span class="text-lg">⚙️</span>
                        </div>
                        <div>
                          <h3
                            class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-11"
                          >
                            Configuração da Campanha
                          </h3>
                          <p
                            class="text-sm text-n-slate-10 dark:text-n-slate-11"
                          >
                            Público-alvo e webhook
                          </p>
                        </div>
                      </div>

                      <div class="space-y-4">
                        <!-- Público-alvo -->
                        <div
                          class="p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <div class="flex items-center gap-2 mb-3">
                            <span
                              class="text-sm font-medium text-gray-700 dark:text-white"
                              >👥 Público-alvo:</span
                            >
                            <span
                              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-500/30"
                            >
                              {{
                                summaryData?.config.selectedLabels?.length || 0
                              }}
                              labels selecionadas
                            </span>
                          </div>

                          <!-- Lista de contatos do público -->
                          <div
                            v-if="publicoData"
                            class="mt-3 p-3 bg-white dark:bg-white/10 rounded border border-gray-200 dark:border-white/30"
                          >
                            <div class="flex items-center justify-between mb-2">
                              <span
                                class="text-sm font-medium text-gray-700 dark:text-white"
                                >Contatos encontrados:</span
                              >
                              <span
                                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30"
                              >
                                {{ publicoData.showing_count }} contatos
                              </span>
                            </div>

                            <!-- Exibir alguns contatos como preview -->
                            <div
                              v-if="
                                publicoData.contacts &&
                                publicoData.contacts.length > 0
                              "
                              class="space-y-2 mt-2"
                            >
                              <div
                                class="text-xs text-gray-500 dark:text-gray-300 mb-1"
                              >
                                Preview dos contatos:
                              </div>
                              <div
                                class="space-y-1 max-h-32 overflow-y-auto custom-scrollbar"
                              >
                                <div
                                  v-for="contact in publicoData.contacts.slice(
                                    0,
                                    5
                                  )"
                                  :key="contact.id"
                                  class="flex items-center justify-between p-2 bg-gray-50 dark:bg-white/5 rounded text-xs border border-gray-100 dark:border-white/10"
                                >
                                  <div
                                    class="flex items-center gap-2 min-w-0 flex-1"
                                  >
                                    <span
                                      class="w-6 h-6 bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 rounded-full flex items-center justify-center text-xs font-bold border border-purple-200 dark:border-purple-500/30"
                                    >
                                      {{
                                        contact.name
                                          ? contact.name.charAt(0).toUpperCase()
                                          : '?'
                                      }}
                                    </span>
                                    <span
                                      class="font-medium text-gray-800 dark:text-white truncate"
                                      >{{ contact.name || 'Sem nome' }}</span
                                    >
                                  </div>
                                  <span
                                    class="text-gray-500 dark:text-gray-300 text-xs ml-2 flex-shrink-0"
                                    >{{ contact.phone_number }}</span
                                  >
                                </div>
                              </div>

                              <div
                                v-if="publicoData.contacts.length > 5"
                                class="text-xs text-gray-500 dark:text-gray-300 text-center italic"
                              >
                                + {{ publicoData.contacts.length - 5 }} contatos
                                adicionais...
                              </div>
                            </div>

                            <div
                              v-else
                              class="text-xs text-gray-500 dark:text-gray-300 italic text-center py-2"
                            >
                              Nenhum contato encontrado com as labels
                              selecionadas
                            </div>
                          </div>

                          <div
                            v-else
                            class="mt-3 p-3 bg-white dark:bg-white/10 rounded border border-gray-200 dark:border-white/30"
                          >
                            <div
                              class="text-xs text-gray-500 dark:text-gray-300 italic text-center"
                            >
                              Carregando dados do público...
                            </div>
                          </div>
                        </div>

                        <!-- Webhook URL -->
                        <div
                          class="p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/20"
                        >
                          <div class="flex items-center gap-2 mb-3">
                            <span
                              class="text-sm font-medium text-gray-700 dark:text-white"
                              >🔗 Webhook URL:</span
                            >
                          </div>

                          <div
                            v-if="summaryData?.config.webhookUrl"
                            class="mt-3 p-3 bg-white dark:bg-white/10 rounded border border-gray-200 dark:border-white/30"
                          >
                            <div
                              class="text-xs text-gray-500 dark:text-gray-300 mb-1"
                            >
                              URL configurada:
                            </div>
                            <div
                              class="text-sm text-gray-800 dark:text-white font-mono bg-gray-100 dark:bg-black/30 p-2 rounded text-xs break-all border border-gray-200 dark:border-white/20"
                            >
                              {{ summaryData.config.webhookUrl }}
                            </div>
                          </div>

                          <div
                            v-else
                            class="mt-3 p-3 bg-white dark:bg-white/10 rounded border border-gray-200 dark:border-white/30"
                          >
                            <div
                              class="text-xs text-gray-500 dark:text-gray-300 italic text-center"
                            >
                              Nenhuma URL de webhook configurada
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Resumo: Mensagem -->
                    <div
                      class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                    >
                      <div
                        class="flex items-center gap-3 pb-4 border-b border-woot-400 dark:border-woot-400 mb-4"
                      >
                        <div
                          class="w-10 h-10 bg-n-teal-3 dark:bg-n-teal-9 rounded-full flex items-center justify-center flex-shrink-0"
                        >
                          <span class="text-lg">💬</span>
                        </div>
                        <div>
                          <h3
                            class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-11"
                          >
                            Conteúdo da Mensagem
                          </h3>
                          <p
                            class="text-sm text-n-slate-10 dark:text-n-slate-11"
                          >
                            Mensagem e anexos da campanha
                          </p>
                        </div>
                      </div>

                      <div class="space-y-3">
                        <div class="flex flex-wrap items-center gap-2 text-sm">
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Caracteres:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-teal-3 dark:bg-n-teal-9 text-n-teal-11 dark:text-n-teal-3 border border-n-teal-6 dark:border-n-teal-7"
                          >
                            {{ summaryData?.message.content?.length || 0 }}
                          </span>
                        </div>

                        <div
                          v-if="summaryData?.message.content"
                          class="p-3 bg-white dark:bg-white/10 rounded border border-n-slate-5 dark:border-n-slate-9"
                        >
                          <span
                            class="font-medium text-n-slate-11 dark:text-n-slate-11"
                            >Mensagem:</span
                          >
                          <p
                            class="mt-1 text-gray-800 dark:text-white text-sm whitespace-pre-wrap"
                          >
                            {{ summaryData.message.content }}
                          </p>
                        </div>

                        <div
                          v-if="summaryData?.message.attachedFiles?.length"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Anexos:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-amber-3 dark:bg-n-amber-9 text-n-amber-11 dark:text-n-amber-3 border border-n-amber-6 dark:border-n-amber-7"
                          >
                            {{ summaryData.message.attachedFiles.length }}
                            arquivo(s)
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Resumo: Agendamento -->
                    <div
                      class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                    >
                      <div
                        class="flex items-center gap-3 pb-4 border-b border-woot-400 dark:border-woot-400 mb-4"
                      >
                        <div
                          class="w-10 h-10 bg-n-iris-3 dark:bg-n-iris-9 rounded-full flex items-center justify-center flex-shrink-0"
                        >
                          <span class="text-lg">📅</span>
                        </div>
                        <div>
                          <h3
                            class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-2"
                          >
                            Agendamento
                          </h3>
                          <p
                            class="text-sm text-n-slate-10 dark:text-n-slate-11"
                          >
                            Quando a campanha será executada
                          </p>
                        </div>
                      </div>

                      <div v-if="summaryData?.schedule" class="space-y-3">
                        <div class="flex flex-wrap items-center gap-2 text-sm">
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Período:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-iris-3 dark:bg-n-iris-9 text-n-iris-11 dark:text-n-iris-3 border border-n-iris-6 dark:border-n-iris-7"
                          >
                            {{ formatDate(summaryData.schedule.startDate) }}
                          </span>
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >até</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-iris-3 dark:bg-n-iris-9 text-n-iris-11 dark:text-n-iris-3 border border-n-iris-6 dark:border-n-iris-7"
                          >
                            {{ formatDate(summaryData.schedule.endDate) }}
                          </span>
                        </div>

                        <div class="flex flex-wrap items-center gap-2 text-sm">
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Horário:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-blue-3 dark:bg-n-blue-9 text-n-blue-11 dark:text-n-blue-3 border border-n-blue-6 dark:border-n-blue-7"
                          >
                            {{ summaryData.schedule.fromTime }}
                          </span>
                        </div>

                        <div class="flex flex-wrap items-center gap-2 text-sm">
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Recorrência:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-amber-3 dark:bg-n-amber-9 text-n-amber-11 dark:text-n-amber-3 border border-n-amber-6 dark:border-n-amber-7"
                          >
                            {{
                              translateRecurrenceType(
                                summaryData.schedule.recurrenceType
                              )
                            }}
                          </span>
                        </div>

                        <div
                          v-if="summaryData?.schedule.scheduledDates?.length"
                          class="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <span class="text-n-slate-10 dark:text-n-slate-11"
                            >Total de execuções:</span
                          >
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-n-teal-3 dark:bg-n-teal-9 text-n-teal-11 dark:text-n-teal-3 border border-n-teal-6 dark:border-n-teal-7 font-bold"
                          >
                            {{ summaryData.schedule.scheduledDates.length }}
                          </span>
                        </div>
                      </div>
                      <div
                        v-else
                        class="text-sm text-n-slate-10 dark:text-n-slate-11 italic"
                      >
                        Nenhum agendamento configurado
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Action Bar -->
              <div class="action-bar">
                <div class="action-bar-background" />

                <!-- Back Button -->
                <div class="back-section">
                  <button
                    type="button"
                    :disabled="isProcessing"
                    class="modern-button back-button"
                    @click="previousTab"
                  >
                    <div class="button-shine" />
                    <ArrowLeft class="button-icon" />
                    <span class="hidden sm:inline">Retornar: Detalhes</span>
                    <span class="sm:hidden">Retornar</span>
                  </button>
                </div>

                <!-- Center Actions -->
                <div class="center-actions" />

                <!-- Primary Actions -->
                <div class="primary-actions">
                  <button
                    type="button"
                    :disabled="!canSubmit || isProcessing"
                    class="modern-button primary-button"
                    @click="submitCampaign"
                  >
                    <div class="primary-gradient" />
                    <div class="primary-glow" />
                    <div class="button-content">
                      <span class="button-text">
                        <span class="hidden sm:inline">Criar Campanha</span>
                        <span class="sm:hidden">Criar</span>
                        <Sparkles class="button-icon ml-2" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  </div>
</template>

<style scoped>
.page-header-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.tab-list {
  @apply grid w-full grid-cols-2 p-1 rounded-lg;
  @apply bg-white dark:bg-slate-900;
  @apply border border-woot-400 dark:border-woot-400;
}

/* Prevent horizontal overflow */
* {
  box-sizing: border-box;
}

.min-w-0 {
  min-width: 0;
}

.break-words {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .page-header-container {
    flex-direction: column;
    gap: 12px;
  }
}

/* Spinner animation - SIMPLIFICADO */
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #16a34a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Custom scrollbar for summary content */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar for summary */
.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #1e293b;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Firefox scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.dark .custom-scrollbar {
  scrollbar-color: #475569 #1e293b;
}

/* Container Principal */
.modern-tab-container {
  @apply relative min-h-[520px] flex flex-col rounded-[20px] border border-woot-400 shadow-lg;
  background: linear-gradient(
    135deg,
    rgb(var(--solid-1)) 0%,
    rgb(var(--solid-2)) 50%,
    rgb(var(--solid-1)) 100%
  );
  box-shadow:
    0 20px 40px rgb(var(--slate-4) / 0.1),
    0 1px 3px rgb(var(--slate-6) / 0.1);
  overflow: hidden;
}

/* Elementos Decorativos de Fundo */
.background-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.decoration-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.6;
  animation: float 8s ease-in-out infinite;
}

.decoration-blob-1 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgb(var(--teal-5) / 0.3), transparent);
  top: -50px;
  right: -50px;
  animation-delay: 0s;
}

.decoration-blob-2 {
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgb(var(--iris-5) / 0.25), transparent);
  bottom: -30px;
  left: -30px;
  animation-delay: 4s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) scale(1);
  }
  50% {
    transform: translateY(-20px) scale(1.1);
  }
}

/* Header */
.tab-header {
  position: relative;
  padding: 32px;
  border-bottom: 1px solid transparent;
  background: linear-gradient(
      90deg,
      transparent,
      rgb(var(--teal-6) / 0.3),
      rgb(var(--iris-6) / 0.3),
      transparent
    )
    bottom;
  background-size: 100% 1px;
  background-repeat: no-repeat;
}

.header-content {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.accent-line {
  width: 4px;
  height: 60px;
  border-radius: 2px;
  background: linear-gradient(180deg, rgb(var(--teal-9)), rgb(var(--iris-9)));
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.8;
    transform: scaleY(1);
  }
  50% {
    opacity: 1;
    transform: scaleY(1.1);
  }
}

.description-text {
  font-size: 16px;
  line-height: 1.6;
  font-weight: 500;
  color: rgb(var(--slate-11));
  background: linear-gradient(
    135deg,
    rgb(var(--slate-11)),
    rgb(var(--slate-9))
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex: 1;
}

/* Main Content */
.main-content-area {
  flex: 1;
  padding: 24px 32px;
  position: relative;
  z-index: 1;
}

/* Action Bar */
.action-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 32px;
  margin: 0 0 4px 0;
  background: linear-gradient(
    135deg,
    rgb(var(--solid-2) / 0.9),
    rgb(var(--solid-3) / 0.8)
  );
  border-top: 1px solid rgb(var(--teal-6) / 0.3);
  border-radius: 0 0 16px 16px;
  backdrop-filter: blur(10px);
}

.action-bar-background {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at 0% 50%,
      rgb(var(--teal-4) / 0.1),
      transparent 50%
    ),
    radial-gradient(
      circle at 100% 50%,
      rgb(var(--iris-4) / 0.1),
      transparent 50%
    );
  pointer-events: none;
}

.back-section {
  flex-shrink: 0;
}

.center-actions {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.primary-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

/* Base Button Styles */
.modern-button {
  position: relative;
  height: 48px;
  padding: 0 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 140px;
  overflow: hidden;
}

.modern-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Button Shine Effect */
.button-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  transition: transform 0.6s;
  pointer-events: none;
}

.modern-button:hover .button-shine {
  transform: translateX(100%);
}

/* Back Button */
.back-button {
  background: linear-gradient(135deg, rgb(var(--solid-2)), rgb(var(--solid-3)));
  color: rgb(var(--slate-11));
  border: 1px solid rgb(var(--border-weak));
}

.back-button:hover {
  background: linear-gradient(135deg, rgb(var(--solid-3)), rgb(var(--slate-3)));
  border-color: rgb(var(--slate-6) / 0.6);
  transform: translateX(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgb(var(--slate-4) / 0.2);
}

/* Primary Button */
.primary-button {
  position: relative;
  color: white;
  font-weight: 700;
  border: none;
}

.primary-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgb(var(--teal-9)),
    rgb(var(--teal-10)),
    rgb(var(--teal-11))
  );
  border-radius: 12px;
  transition: all 0.3s;
}

.primary-glow {
  position: absolute;
  inset: -4px;
  background: linear-gradient(
    135deg,
    rgb(var(--teal-6) / 0.6),
    rgb(var(--teal-8) / 0.4)
  );
  border-radius: 16px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
  filter: blur(12px);
}

.button-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.primary-button:hover {
  transform: translateY(-2px) scale(1.05);
}

.primary-button:hover .primary-gradient {
  background: linear-gradient(
    135deg,
    rgb(var(--teal-10)),
    rgb(var(--teal-11)),
    rgb(var(--teal-12))
  );
  animation: gradient-shift 2s ease-in-out infinite;
}

.primary-button:hover .primary-glow {
  opacity: 1;
}

@keyframes gradient-shift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.primary-button:active {
  transform: translateY(0) scale(1.02);
}

/* Button Elements */
.button-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.button-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover .button-icon {
  transform: translateX(-2px);
}

.primary-button:hover .button-icon {
  transform: translateX(2px) scale(1.1);
}

/* Responsive */
@media (max-width: 640px) {
  .modern-tab-container {
    min-height: 480px;
    border-radius: 16px;
  }

  .tab-header,
  .main-content-area,
  .action-bar {
    padding: 20px;
  }

  .action-bar {
    flex-direction: column;
    gap: 12px;
  }

  .back-section,
  .primary-actions {
    width: 100%;
  }

  .primary-actions {
    flex-direction: column;
  }

  .modern-button {
    min-width: 100%;
    height: 52px;
    font-size: 16px;
  }

  .description-text {
    font-size: 15px;
  }
}

/* Dark Mode */
@media (prefers-color-scheme: dark) {
  .modern-tab-container {
    background: linear-gradient(
      135deg,
      rgb(var(--solid-1) / 0.95) 0%,
      rgb(var(--solid-2) / 0.9) 50%,
      rgb(var(--solid-1) / 0.95) 100%
    );
    box-shadow:
      0 20px 40px rgb(var(--slate-12) / 0.3),
      0 1px 3px rgb(var(--slate-12) / 0.2);
  }

  .description-text {
    color: rgb(var(--slate-9));
    background: linear-gradient(
      135deg,
      rgb(var(--slate-9)),
      rgb(var(--slate-8))
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .action-bar {
    background: linear-gradient(
      135deg,
      rgb(var(--solid-2) / 0.8),
      rgb(var(--solid-3) / 0.7)
    );
    border-top-color: rgb(var(--teal-7) / 0.4);
  }

  .back-button {
    color: rgb(var(--slate-9));
  }
}

/* Custom styling for tabs to match the design theme */
:deep(.tabs-trigger) {
  transition: all 0.2s ease-in-out;
}

:deep(.tabs-trigger[data-state='active']) {
  background: white;
  border: 1px solid rgb(34 197 94);
  color: rgb(21 128 61);
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
}

:deep(.tabs-trigger[data-state='active']) .dark {
  background: rgb(15 23 42);
  color: rgb(74 222 128);
}
</style>
